import { useState, useEffect } from 'react';

import ResponsiveFlightGuestBox from '@components/ui/guest-box/guest-box-flight/ResponsiveFlightGuestBox';
import { AirportSearchViewModel } from '@api/index';
import RadioGroup from '@components/RadioGroup';
import useSearchBar from '@components/views/flight/hooks/useSearchBar';
import {
  FlightTripType,
  FlightGuestProps,
} from '@components/views/flight/types';
import ResponsiveAirportSearch from '@components/views/flight/listing/SearchBar/AirportSearch/ResponsiveAirportSearch';
import { ChangeVertical, PlaneLanding, PlaneTakeOff } from '@assets/icons';
import CustomIcon from '@components/utils/CustomIcon';
import { DateType, formatMomentDate, URL_DATE_FORMAT } from '@utils/dateUtil';
import {
  FlightTripOption,
  flightTripOptions,
  FLIGHT_SEARCH_QUERY_PARAM_KEYS,
  INBOUND_FLIGHT_FILTER_QUERY_PARAM_KEYS,
  initialGuestInfo,
  OUTBOUND_FLIGHT_FILTER_QUERY_PARAM_KEYS,
} from '@components/views/flight/constants';
import { getGuestText, isDomesticFlight } from '@components/views/flight/utils';
import Checkbox from '@components/Checkbox';
import { InitialFormValues } from '@components/views/types';
import { LocationAnimationErrorBox } from '@components/views/components/listing/ListingStyle';
import { useDevice } from '@contexts/device';
import { flight_ga4Event } from '@utils/ga4Event/flight_ga4Event';

import BookerDatePicker from './FlightBookerDatePicker';
import { urlByFilterItems } from './Booker';
import BookerCheckboxContainer from './common/BookerCheckboxContainer';
import { BookerHr } from './common/styled';
import {
  AirportSearchWrapper,
  IconWrapper,
  RadioGroupcontainer,
  ResponsiveGuestListingContainer,
  StyledAirportSearch,
  StyledBookerFlightGuestBox,
} from './style';

export const getLabel = (option: FlightTripOption) => option.label;
export const getValue = (option: FlightTripOption) => option.value;

const DOMESTIC_FLIGHT_PAGE = 'ic-hat-ucuslari';
const ABROAD_FLIGHT_PAGE = 'dis-hat-ucuslari';
export interface FlightBookerError {
  location?: {
    departure?: boolean;
    arriving?: boolean;
  };
  date?: boolean;
}

interface FlightTabProps {
  onHrefUpdate: (href: string) => void;
  initialValues?: InitialFormValues;
  setError?: React.Dispatch<
    React.SetStateAction<Partial<FlightBookerError | undefined>>
  >;
  setInitialError?: React.Dispatch<
    React.SetStateAction<Partial<FlightBookerError | undefined>>
  >;
  error?: FlightBookerError;
  initialError?: FlightBookerError;
  href?: string;
  isBookerSearch?: boolean;
  isFlightTicketPage?: boolean;
  onSearchCalled?: boolean;
  borderRadius?: string;
  isTravelPlanning?: boolean;
  isHomePage?: boolean;
  isHoliday?: boolean;
}

const FlightTabContent = ({
  onHrefUpdate,
  initialValues,
  setError,
  error,
  setInitialError,
  href,
  isBookerSearch = false,
  isFlightTicketPage,
  onSearchCalled,
  borderRadius,
  isTravelPlanning,
  isHomePage,
  isHoliday,
}: FlightTabProps) => {
  const [tripType, setTripType] = useState<FlightTripType>('round-trip');
  const [isDirectFlights, setIsDirectFlights] = useState(false);

  const [hrefProps, setHrefProps] = useState<{
    [key: string]: string | number | undefined;
  }>({
    [FLIGHT_SEARCH_QUERY_PARAM_KEYS.isOneWay]: 'false',
    [FLIGHT_SEARCH_QUERY_PARAM_KEYS.isDirectFlight]: 'false',
    [FLIGHT_SEARCH_QUERY_PARAM_KEYS.adults]:
      initialGuestInfo.numberOfAdultGuests,
    [FLIGHT_SEARCH_QUERY_PARAM_KEYS.departureDate]: formatMomentDate(
      initialValues?.startDate,
      URL_DATE_FORMAT
    ),
    [FLIGHT_SEARCH_QUERY_PARAM_KEYS.returnDate]:
      tripType === 'round-trip'
        ? formatMomentDate(initialValues?.endDate, URL_DATE_FORMAT)
        : undefined,
  });
  const { isDesktop } = useDevice();
  const {
    departureAirportSearchText,
    onDepartureAirportTextChange,
    onDepartureAirportSelect,
    arrivalAirportSearchText,
    onArrivalAirportTextChange,
    departureAirport,
    arrivalAirport,
    onArrivalAirportSelect,
    guestInfo,
    onApplyGuestsForm,
    onDatesChange,
  } = useSearchBar();

  const handleDepartureAirportSelect = (airport?: AirportSearchViewModel) => {
    const _isDomesticFlight = isDomesticFlight(airport, arrivalAirport);
    const props = {
      ...hrefProps,
      [FLIGHT_SEARCH_QUERY_PARAM_KEYS.departureAirportCode]:
        airport?.airportCode,
      [FLIGHT_SEARCH_QUERY_PARAM_KEYS.isDomestic]: _isDomesticFlight
        ? 'true'
        : 'false',
    };
    if (airport?.isCity) {
      props[FLIGHT_SEARCH_QUERY_PARAM_KEYS.departureIsCity] = 1;
    } else {
      props[FLIGHT_SEARCH_QUERY_PARAM_KEYS.departureIsCity] = '0';
    }
    onDepartureAirportSelect(airport);
    setHrefProps(props);
    onHrefUpdate(
      urlByFilterItems(
        _isDomesticFlight ? DOMESTIC_FLIGHT_PAGE : ABROAD_FLIGHT_PAGE,
        props
      )
    );
  };

  const handleArrivalAirportSelect = (airport?: AirportSearchViewModel) => {
    const _isDomesticFlight = isDomesticFlight(airport, arrivalAirport);
    const props = {
      ...hrefProps,
      [FLIGHT_SEARCH_QUERY_PARAM_KEYS.arrivalAirportCode]: airport?.airportCode,
      [FLIGHT_SEARCH_QUERY_PARAM_KEYS.isDomestic]: isDomesticFlight(
        departureAirport,
        airport
      )
        ? 'true'
        : 'false',
    };
    if (airport?.isCity) {
      props[FLIGHT_SEARCH_QUERY_PARAM_KEYS.arrivalIsCity] = 1;
    } else {
      props[FLIGHT_SEARCH_QUERY_PARAM_KEYS.arrivalIsCity] = '0';
    }
    onArrivalAirportSelect(airport);
    setHrefProps(props);
    onHrefUpdate(
      urlByFilterItems(
        _isDomesticFlight ? DOMESTIC_FLIGHT_PAGE : ABROAD_FLIGHT_PAGE,
        props
      )
    );
  };

  const handleDatesChange = (dates?: {
    startDate?: DateType | null;
    endDate?: DateType | null;
  }) => {
    onDatesChange({
      startDate: dates?.startDate || null,
      endDate: dates?.endDate || null,
    });
    const props = {
      ...hrefProps,
      [FLIGHT_SEARCH_QUERY_PARAM_KEYS.departureDate]: formatMomentDate(
        dates?.startDate,
        URL_DATE_FORMAT
      ),
      [FLIGHT_SEARCH_QUERY_PARAM_KEYS.returnDate]:
        tripType === 'round-trip'
          ? formatMomentDate(dates?.endDate, URL_DATE_FORMAT)
          : undefined,
    };

    setHrefProps(props);
    onHrefUpdate(
      urlByFilterItems(
        props[FLIGHT_SEARCH_QUERY_PARAM_KEYS.isDomestic] === 'true'
          ? DOMESTIC_FLIGHT_PAGE
          : ABROAD_FLIGHT_PAGE,
        props
      )
    );
  };

  useEffect(() => {
    if (initialValues) {
      handleDatesChange({
        startDate: initialValues.startDate,
        endDate: initialValues.endDate,
      });

      initialValues.guestInfo &&
        handleSubmitGuestForm({
          numberOfAdultGuests: initialValues.guestInfo.numberOfAdultGuests,
          numberOfChildGuests: initialValues.guestInfo.numberOfChildGuests,
          numberOfInfantGuests:
            initialValues.guestInfo.numberOfInfantGuests || 0,
        });
    }
  }, []);

  const handleSubmitGuestForm = (params: FlightGuestProps | undefined) => {
    const props = {
      ...hrefProps,
      [FLIGHT_SEARCH_QUERY_PARAM_KEYS.adults]: params?.numberOfAdultGuests,
      [FLIGHT_SEARCH_QUERY_PARAM_KEYS.childs]: params?.numberOfChildGuests,
      [FLIGHT_SEARCH_QUERY_PARAM_KEYS.infants]: params?.numberOfInfantGuests,
    };
    params && onApplyGuestsForm(params);
    setHrefProps(props);
    onHrefUpdate(
      urlByFilterItems(
        props[FLIGHT_SEARCH_QUERY_PARAM_KEYS.isDomestic] === 'true'
          ? DOMESTIC_FLIGHT_PAGE
          : ABROAD_FLIGHT_PAGE,
        props
      )
    );
  };

  const onChangeIsDirectFlight = () => {
    const nextIsDirectFlight = !isDirectFlights;
    const props = {
      ...hrefProps,
      [FLIGHT_SEARCH_QUERY_PARAM_KEYS.isDirectFlight]: nextIsDirectFlight
        ? 'true'
        : 'false',
      [OUTBOUND_FLIGHT_FILTER_QUERY_PARAM_KEYS.STOPS]: nextIsDirectFlight
        ? 1
        : undefined,
      [INBOUND_FLIGHT_FILTER_QUERY_PARAM_KEYS.STOPS]:
        nextIsDirectFlight && tripType === 'round-trip' ? 1 : undefined,
    };
    setIsDirectFlights(nextIsDirectFlight);
    setHrefProps(props);
    onHrefUpdate(
      urlByFilterItems(
        props[FLIGHT_SEARCH_QUERY_PARAM_KEYS.isDomestic] === 'true'
          ? DOMESTIC_FLIGHT_PAGE
          : ABROAD_FLIGHT_PAGE,
        props
      )
    );
  };

  const onChangeTripType = (tripType: FlightTripType) => {
    const props = {
      ...hrefProps,
      [FLIGHT_SEARCH_QUERY_PARAM_KEYS.isOneWay]:
        tripType === 'one-way' ? 'true' : 'false',
      [FLIGHT_SEARCH_QUERY_PARAM_KEYS.returnDate]:
        tripType === 'round-trip'
          ? hrefProps[FLIGHT_SEARCH_QUERY_PARAM_KEYS.returnDate]
          : undefined,
    };
    setTripType(tripType);
    setHrefProps(props);
    onHrefUpdate(
      urlByFilterItems(
        props[FLIGHT_SEARCH_QUERY_PARAM_KEYS.isDomestic] === 'true'
          ? DOMESTIC_FLIGHT_PAGE
          : ABROAD_FLIGHT_PAGE,
        props
      )
    );
  };

  useEffect(() => {
    if (!departureAirportSearchText) {
      setInitialError?.((val) => ({
        ...val,
        location: { ...val?.location, departure: true },
      }));
    } else {
      setInitialError?.((val) => ({
        ...val,
        location: { ...val?.location, departure: false },
      }));

      setError?.((val) => ({
        ...val,
        location: { ...val?.location, departure: false },
      }));
    }

    if (!arrivalAirportSearchText) {
      setInitialError?.((val) => ({
        ...val,
        location: { ...val?.location, arriving: true },
      }));
    } else {
      setInitialError?.((val) => ({
        ...val,
        location: { ...val?.location, arriving: false },
      }));

      setError?.((val) => ({
        ...val,
        location: { ...val?.location, arriving: false },
      }));
    }

    if (tripType === 'one-way') {
      if (!href?.includes('ddate')) {
        setInitialError?.((val) => ({
          ...val,
          date: true,
        }));
      } else {
        setInitialError?.((val) => ({
          ...val,
          date: false,
        }));

        setError?.((val) => ({
          ...val,
          date: false,
        }));
      }
    } else {
      if (!href?.includes('ddate') || !href?.includes('rdate')) {
        setInitialError?.((val) => ({
          ...val,
          date: true,
        }));
      } else {
        setInitialError?.((val) => ({
          ...val,
          date: false,
        }));

        setError?.((val) => ({
          ...val,
          date: false,
        }));
      }
    }
  }, [href]);

  const swapAirports = () => {
    const props = {
      ...hrefProps,
      [FLIGHT_SEARCH_QUERY_PARAM_KEYS.departureAirportCode]:
        arrivalAirport?.airportCode,
      [FLIGHT_SEARCH_QUERY_PARAM_KEYS.arrivalAirportCode]:
        departureAirport?.airportCode,
    };
    onArrivalAirportSelect(departureAirport);
    onDepartureAirportSelect(arrivalAirport);
    handleDepartureAirportSelect(arrivalAirport);
    handleArrivalAirportSelect(departureAirport);
    setHrefProps(props);
    onHrefUpdate(
      urlByFilterItems(
        props[FLIGHT_SEARCH_QUERY_PARAM_KEYS.isDomestic] === 'true'
          ? DOMESTIC_FLIGHT_PAGE
          : ABROAD_FLIGHT_PAGE,
        props
      )
    );
  };

  const [flightGa4Data, setFlightGa4Data] = useState({
    destination_point: '',
    starting_point: '',
    arrival_date: '',
    departure_date: '',
    is_one_way: '',
    is_direct: '',
  });
  useEffect(() => {
    if (
      arrivalAirport?.airportName &&
      departureAirport?.airportName &&
      hrefProps[FLIGHT_SEARCH_QUERY_PARAM_KEYS.departureDate]
    ) {
      setFlightGa4Data({
        ...flightGa4Data,
        destination_point: arrivalAirport.airportName,
        starting_point: departureAirport.airportName,
        arrival_date:
          hrefProps[FLIGHT_SEARCH_QUERY_PARAM_KEYS.returnDate]?.toString() ??
          '',
        departure_date:
          hrefProps[FLIGHT_SEARCH_QUERY_PARAM_KEYS.departureDate]?.toString() ??
          '',
        is_one_way: tripType === 'one-way' ? 'Tek Yön' : 'Gidiş-Dönüş',
        is_direct: isDirectFlights ? 'Direkt' : 'Aktarmalı',
      });
    }
  }, [href]);
  useEffect(() => {
    if (onSearchCalled) {
      flight_ga4Event(flightGa4Data);
    }
  }, [onSearchCalled]);
  return (
    <>
      <RadioGroupcontainer isFlightTicketPage={isFlightTicketPage}>
        <RadioGroup
          options={flightTripOptions}
          selectedIndex={0}
          direction='row'
          getLabel={getLabel}
          getValue={getValue}
          onChange={onChangeTripType}
        />
      </RadioGroupcontainer>
      <AirportSearchWrapper borderRadius={borderRadius}>
        {isDesktop ? (
          <StyledAirportSearch
            departureAirportError={error?.location?.departure}
            arrivalAirportError={error?.location?.arriving}
            departureAirportSearchText={departureAirportSearchText}
            onDepartureAirportTextChange={onDepartureAirportTextChange}
            onDepartureAirportSelect={handleDepartureAirportSelect}
            arrivalAirportSearchText={arrivalAirportSearchText}
            onArrivalAirportTextChange={onArrivalAirportTextChange}
            onArrivalAirportSelect={handleArrivalAirportSelect}
            swapAirports={swapAirports}
            isDateError={false}
            borderRadius={borderRadius}
            isHomePage={isHomePage}
          />
        ) : (
          <>
            <div
              style={{
                width: '100%',
                paddingLeft: '15px',
              }}
            >
              <LocationAnimationErrorBox
                borderRadius={borderRadius}
                hasError={error?.location?.departure || false}
              >
                <ResponsiveAirportSearch
                  onAirportSelect={handleDepartureAirportSelect}
                  title='Nereden Gideceksiniz?'
                  subTitle={
                    departureAirport
                      ? departureAirport?.airportName +
                        ', ' +
                        departureAirport?.airportCode
                      : ''
                  }
                  placeholder='Havalimanı veya şehir'
                  drawerTitle='Uçuş Arama - Gidiş'
                  icon={PlaneTakeOff}
                />
              </LocationAnimationErrorBox>

              <LocationAnimationErrorBox
                borderRadius={borderRadius}
                hasError={error?.location?.arriving || false}
              >
                <ResponsiveAirportSearch
                  onAirportSelect={handleArrivalAirportSelect}
                  title='Nereye Gideceksiniz?'
                  subTitle={
                    arrivalAirport
                      ? arrivalAirport?.airportName +
                        ', ' +
                        arrivalAirport?.airportCode
                      : ''
                  }
                  placeholder='Havalimanı veya şehir'
                  drawerTitle='Uçuş Arama - Dönüş'
                  icon={PlaneLanding}
                />
              </LocationAnimationErrorBox>
            </div>
            <div>
              <IconWrapper>
                <CustomIcon
                  icon={ChangeVertical}
                  fontSize={20}
                  paletteColor='darkGrey'
                  onClick={swapAirports}
                />
              </IconWrapper>
            </div>
          </>
        )}
      </AirportSearchWrapper>
      {isDesktop ? <BookerHr isTravelPlanning={isTravelPlanning} /> : null}
      <LocationAnimationErrorBox
        borderRadius={borderRadius}
        hasError={error?.date || false}
      >
        <BookerDatePicker
          initialDates={{
            startDate: initialValues?.startDate,
            endDate: initialValues?.endDate,
          }}
          isHoliday={isHoliday}
          // error={error?.date || false}
          dateChange={handleDatesChange}
          tripType={tripType}
        />
      </LocationAnimationErrorBox>
      <BookerHr isTravelPlanning={isTravelPlanning} />
      {isDesktop ? (
        <StyledBookerFlightGuestBox
          onApplyGuestsForm={handleSubmitGuestForm}
          guestInfo={guestInfo || initialGuestInfo}
          isBookerSearch={isBookerSearch}
          isControlDisable={false}
          isDateError={false}
          borderRadius={borderRadius}
        />
      ) : (
        <ResponsiveGuestListingContainer>
          <ResponsiveFlightGuestBox
            guestInfo={guestInfo || initialGuestInfo}
            onApplyGuestsForm={handleSubmitGuestForm}
            title='Kaç kişi yolculuk yapacaksınız?'
            subTitle={getGuestText(guestInfo)}
            placeholder='Yolcu Seçimi'
            drawerTitle='Yolcu Seçimi'
            arrowIcon={true}
          />
        </ResponsiveGuestListingContainer>
      )}
      <BookerCheckboxContainer
        justifyContent='flex-end'
        rightSection={
          <Checkbox
            title='Direkt Uçuşları Göster'
            onChange={onChangeIsDirectFlight}
            checked={isDirectFlights}
          />
        }
      />
    </>
  );
};
export default FlightTabContent;
